import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../client";
import { qk } from "../keys";
import { clearSession, markSignedIn } from "../../auth/session";

export interface TokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
}
/** admins.role 어휘 — 서버 app/domains/auth/deps.py 의 Role(StrEnum)과 짝. */
export type AdminRole = "ADMIN" | "CALL_CENTER" | "CHRGR_MAKER" | "CONSIGNOR" | "APP";

export interface AdminMe {
  id: number;
  lgn_id: string | null;
  role: AdminRole | null;
  co_nm: string | null;
  manager_nm: string | null;
  manager_email: string | null;
  manager_cttpc: string | null;
  reg_dt?: string | null;
  last_lgn_dt?: string | null;
  /** 소속 — 제조사 이름(CHRGR_MAKER), 연결된 운영그룹 이름들(corp_admins). 읽기 전용. */
  maker_nm?: string | null;
  corp_nms?: string[];
  /** 연결된 운영그룹 id 들. 하나뿐이면 새 충전소의 운영그룹 기본값(2026-11-02). */
  corp_ids?: number[];
}

/** POST /auth/login → 토큰 저장. */
export function useLogin() {
  return useMutation({
    mutationFn: async (body: { lgn_id: string; password: string }) => {
      const { data } = await api.post<TokenResponse>("/auth/login", body);
      return data;
    },
    // 토큰은 쿠키로 온다. 여기서는 로그인했다는 표시만 남긴다.
    onSuccess: () => markSignedIn(),
  });
}

/** GET /auth/me — 현재 운영자. */
export function useMe() {
  return useQuery({
    queryKey: qk.me,
    queryFn: async () => (await api.get<AdminMe>("/auth/me")).data,
  });
}

/** PUT /auth/me — 안 보낸 칸은 그대로. lgn_id·role 은 요청에 아예 없다. */
export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: Partial<Pick<AdminMe, "co_nm" | "manager_nm" | "manager_email" | "manager_cttpc">>) =>
      (await api.put<AdminMe>("/auth/me", body)).data,
    onSuccess: (me) => qc.setQueryData(qk.me, me),
  });
}

/** POST /auth/me/password — 403 WRONG_PASSWORD = 현재 비밀번호 불일치. */
export function useChangePassword() {
  return useMutation({
    mutationFn: async (body: { current_password: string; new_password: string }) =>
      (await api.post("/auth/me/password", body)).data,
  });
}

export function logout() {
  // 서버에도 알린다. 쿠키를 지우는 자리가 없으면 로그아웃해도 쿠키가 남는다.
  api.post("/auth/logout").catch(() => undefined).finally(() => {
    clearSession();
    location.assign("/login");
  });
}

/** 현재 운영자가 ADMIN 롤인지. 완전삭제 등 상위권한 UI 노출 판단용. */
export function useIsAdmin(): boolean {
  const { data } = useMe();
  return data?.role === "ADMIN";
}
